import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TextField, Button, Typography, Box, Card, CardContent,
  Link, Alert, CircularProgress, Divider } from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import FacebookIcon from "@mui/icons-material/Facebook";
import AppsIcon from "@mui/icons-material/Apps";

export default function LoginPage() {
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const registered = params.get('registered');
  const socialError = params.get('socialError');
  const socialPending = params.get('socialPendingApproval');

  // Sosyal giriş: whitelist'li devplatform üzerinden doğrulanır (relay), dönüşte
  // bulunulan siteye (returnTo) /auth/social-success token ile geri döner.
  const socialLogin = (provider) => {
    const relay = "https://devplatform.biyapayzeka.com";
    window.location.href = `${relay}/api/platform/auth/${provider}?returnTo=${encodeURIComponent(window.location.origin)}`;
  };

  const handleLogin = async () => {
    if (!emailOrUsername || !password) { setError("Email ve şifre gerekli"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch("/api/platform/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emailOrUsername, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "Giriş başarısız"); return; }
      localStorage.setItem("biyz_token", data.token);
      localStorage.setItem("biyz_user", JSON.stringify(data.user || {}));
      // ?next=/packages?... gibi guvenli (site-ici, "/" ile baslayan) bir hedef varsa oraya git,
      // yoksa eskisi gibi /chat. (Acik yonlendirme riskine karsi sadece ayni-site relative path kabul edilir.)
      const nextParam = params.get("next");
      if (nextParam && nextParam.startsWith("/") && !nextParam.startsWith("//")) {
        navigate(nextParam);
      } else {
        navigate("/chat");
      }
    } catch { setError("Sunucuya bağlanılamadı"); }
    finally { setLoading(false); }
  };

  return (
    <Box sx={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#ffffff",
      px: 2,
    }}>
      <Card elevation={4} sx={{
        width: "100%",
        maxWidth: 440,
        borderRadius: 3,
        overflow: "visible",
        position: "relative",
        pt: 2,
      }}>
        <Box sx={{ display:"flex", justifyContent:"center", mb: 1 }}>
          <Box component="img" src="/bilogo.png?v=b2" alt="bi AI"
            sx={{ width: 64, height: 64, borderRadius: "50%",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                  border: "3px solid white" }} />
        </Box>

        <CardContent sx={{ pb: 4, px: 4 }}>
          <Typography variant="h5" align="center" fontWeight={600}
            sx={{ mb: 3, color: "#1a1a2e" }}>
            Hoş Geldiniz
          </Typography>

          {registered && <Alert severity="success" sx={{ mb: 2 }}>Kayıt başarılı!</Alert>}
          {socialError && <Alert severity="error" sx={{ mb: 2 }}>Sosyal medya ile giriş başarısız oldu. Lütfen tekrar deneyin.</Alert>}
          {socialPending && <Alert severity="info" sx={{ mb: 2 }}>Hesabınız oluşturuldu, onay bekleniyor. Onaylandığında giriş yapabilirsiniz.</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <TextField
            fullWidth label="Email veya Kullanıcı Adı"
            variant="outlined" margin="normal"
            value={emailOrUsername}
            onChange={(e) => setEmailOrUsername(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
            autoFocus
          />
          <TextField
            fullWidth label="Şifre" type="password"
            variant="outlined" margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />

          <Button
            fullWidth variant="contained" size="large"
            onClick={handleLogin} disabled={loading}
            sx={{
              mt: 2.5, mb: 1.5, py: 1.5,
              fontWeight: 700, fontSize: 15,
              letterSpacing: 1.5,
              background: "#1565c0",
              "&:hover": { background: "#0d47a1" }
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "GİRİŞ YAP"}
          </Button>

          <Box sx={{ textAlign: "center", mb: 1 }}>
            <Link href="/password-reset" variant="body2"
              sx={{ color: "#1565c0", textDecoration: "none" }}>
              Şifremi Unuttum
            </Link>
          </Box>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography variant="body2" color="text.secondary" component="span">
              Hesabın yok mu?{" "}
            </Typography>
            <Link href="/register" variant="body2"
              sx={{ color: "#1565c0", fontWeight: 600, textDecoration: "none" }}>
              Kaydol
            </Link>
          </Box>

          <Divider>
            <Typography variant="caption" color="text.secondary">
              Sosyal Medya ile Giriş Yap
            </Typography>
          </Divider>

          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "center" }}>
            <Button variant="outlined" startIcon={<GoogleIcon />}
              onClick={() => socialLogin("google")}
              sx={{ borderRadius: 2, flex: 1, textTransform: "none",
                    borderColor: "#e0e0e0", color: "#555", fontSize: 12 }}>
              GOOGLE
            </Button>
            <Button variant="outlined" startIcon={<AppsIcon />}
              sx={{ borderRadius: 2, flex: 1, textTransform: "none",
                    borderColor: "#e0e0e0", color: "#555", fontSize: 12 }}>
              MICROSOFT
            </Button>
            <Button variant="outlined" startIcon={<FacebookIcon />}
              onClick={() => socialLogin("facebook")}
              sx={{ borderRadius: 2, flex: 1, textTransform: "none",
                    borderColor: "#e0e0e0", color: "#555", fontSize: 12 }}>
              FACEBOOK
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
